<template>
  <StickyBottomButton
    v-if="configuratorStore.currentStep === ConfiguratorStep.ProductPage"
  >
    <DefaultButton
      v-if="configuratorStore.mediaclipLaunched"
      id="order-button"
      @click="configuratorStore.setConfiguratorStep"
    >
      <span v-t="'pages.product_detail_page.continue_button.text'" />
    </DefaultButton>

    <DefaultButton
      v-else
      id="order-button"
      ref="uploadButton"
      :loading="isLoading"
      class="w-full gap-0.5"
      @click="open"
    >
      <template #icon>
        <UploadIcon class="h-6 animate-fast-pulse text-white" />
      </template>

      <template #default>
        <span
          v-if="isOverDropZone"
          v-t="'pages.product_detail_page.dropzone.text'"
        />
        <span v-else v-t="'pages.product_detail_page.upload_button.text'" />
      </template>
    </DefaultButton>
  </StickyBottomButton>

  <FullPageLoader
    v-if="showLoader"
    :done="configuratorStore.currentStep !== ConfiguratorStep.ProductPage"
    :current-step="currentStep"
    @destroy-me="showLoader = false"
  />
</template>

<script setup lang="ts">
import StickyBottomButton from '@/components/Buttons/StickyBottomButton.vue';
import FullPageLoader from '@/components/States/FullPageLoader.vue';
import UploadIcon from '@/components/Icons/UploadIcon.vue';
import { useImage } from '@/composables/UseImage';
import { useMediaclipCommunication } from '@/composables/UseMediaclipCommunication';
import I18n from '@/i18n';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import { cutoutKey, useFilterStore } from '@/stores/Configurator/FilterStore';
import { ConfiguratorStep } from '@/types/Configurator/ConfiguratorStep';
import { useDropZone, useFileDialog } from '@vueuse/core';
import _ from 'lodash';
import { computed, ref } from 'vue';
import DefaultButton from '@/components/Buttons/DefaultButton.vue';

const uploadButton = ref<HTMLButtonElement>();
const isLoading = ref(false);
const showLoader = ref(false);

const acceptedImageTypes = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/bmp',
  'image/webp',
];

const genericMessage = I18n.global.t(
  'messages.error.upload_button.upload_error'
);
const brokenImageMessage = I18n.global.t(
  'messages.error.upload_button.broken_image_error'
);
const imageFormatsMessage = I18n.global.t(
  'messages.error.upload_button.image_formats_error',
  {
    formats: 'png, jpg, jpeg, bmp, webp',
  }
);

function showError(message: string) {
  window.dispatchMessages(
    [
      {
        type: 'error',
        text: message,
      },
    ],
    10000
  );
}

const { isOverDropZone } = useDropZone(uploadButton, {
  dataTypes: acceptedImageTypes,
  onDrop(files: File[] | null) {
    if (files) {
      const acceptedFile = files.find(file =>
        acceptedImageTypes.includes(file.type)
      );
      if (acceptedFile) {
        onFileUpload(acceptedFile);
      } else {
        showError(imageFormatsMessage);
      }
    }
  },
});

const { open, onChange } = useFileDialog({
  accept: acceptedImageTypes.join(','),
  directory: false,
  multiple: false,
});

onChange((files: FileList | null) => {
  if (files) {
    onFileUpload(files[0]);
  }
});

const configuratorStore = useConfiguratorStore();
const filterStore = useFilterStore();
const imageComposable = useImage();
const mediaclipCommunicationComposable = useMediaclipCommunication();

async function onFileUpload(file: File) {
  if (!hasValidFileSize(file)) {
    showError(genericMessage);
    return;
  }

  // Set the selected filter option for cutout filter
  if (_.has(filterStore.filterOptions, cutoutKey)) {
    const imageDimensions = await imageComposable.getImageDimensions(file);

    // Check if width and height are not undefined
    if (!_.every(imageDimensions, value => value !== undefined)) {
      showError(brokenImageMessage);
      return;
    }

    filterStore.setDefaultFilterSku(cutoutKey, imageDimensions);
  }

  isLoading.value = true;
  showLoader.value = true;

  try {
    // TODO: Can we clean up this code?
    await mediaclipCommunicationComposable.requestToken();
    await mediaclipCommunicationComposable.uploadPhoto(file);

    configuratorStore.mediaclipInitialized = true;
    configuratorStore.setConfiguratorStep();
    isLoading.value = false;
    showLoader.value = false;
    return;
  } catch {
    console.debug(`Failed uploading photo.`);
  }

  // If we get here, we failed to upload the image
  isLoading.value = false;
  showLoader.value = false;

  showError(genericMessage);
}

function hasValidFileSize(file: File): boolean {
  // Default local size in bytes (8MB)
  let maxFileSize = 8_388_608;

  if (import.meta.env.PROD) {
    // Production max size 100MB (limited by Mediaclip)
    maxFileSize = 104_856_600;
  }

  return file.size < maxFileSize;
}

const currentStep = computed(() => {
  if (
    !configuratorStore.mediaclipProjectId ||
    !configuratorStore.mediaclipToken
  ) {
    return I18n.global.t('loader.currentstep.uploading');
  }
  if (
    configuratorStore.mediaclipProjectId &&
    configuratorStore.mediaclipToken
  ) {
    return I18n.global.t('loader.completedstep.uploading');
  }
  if (!configuratorStore.mediaclipInitialized) {
    return I18n.global.t('loader.currentstep.initializing');
  }
  if (configuratorStore.mediaclipInitialized) {
    return I18n.global.t('loader.completedstep.initializing');
  }
  return I18n.global.t('loader.currentstep.starting_tool');
});
</script>
